import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import styles from './Toast.module.css';

export type ToastVariant = 'achievement' | 'success' | 'error';

interface ToastProps {
  visible: boolean;
  variant: ToastVariant;
  message: string;
  onDismiss: () => void;
}

function AchievementIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

function ErrorIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <line x1="15" y1="9" x2="9" y2="15" />
      <line x1="9" y1="9" x2="15" y2="15" />
    </svg>
  );
}

function DismissIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

const icons: Record<ToastVariant, React.ReactNode> = {
  achievement: <AchievementIcon />,
  success: <CheckIcon />,
  error: <ErrorIcon />,
};

export function Toast({ visible, variant, message, onDismiss }: ToastProps) {
  const { t: tCommon } = useTranslation('common');

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className={`${styles.toast} ${styles[variant]}`}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          role={variant === 'error' ? 'alert' : 'status'}
          aria-live={variant === 'error' ? 'assertive' : 'polite'}
        >
          <span className={styles.icon}>{icons[variant]}</span>
          <p className={styles.message}>{message}</p>
          <button
            className={styles.dismiss}
            onClick={onDismiss}
            aria-label={tCommon('dismiss')}
          >
            <DismissIcon />
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
